// Single-series line chart (follower growth): 2px line, 10% area wash, end-dot + end label,
// hairline grid, crosshair + tooltip that snaps to the nearest day (pointer and arrow keys).

import { useMemo, useState, type KeyboardEvent, type PointerEvent } from "react";
import { ChartTooltip, useWidth } from "./ChartCard";
import { CHROME, SERIES_1 } from "./palette";
import { axisLabel, linear, niceTicks, sparseLabelIndices } from "./scale";

export interface LinePoint {
  key: string;
  label: string;
  value: number | null;
}

const HEIGHT = 220;
const PAD = { top: 16, right: 56, bottom: 28, left: 44 };

export function LineChart({ points, seriesLabel, format = (v) => v.toLocaleString("en-US") }: { points: LinePoint[]; seriesLabel: string; format?: (v: number) => string }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  const [active, setActive] = useState<number | null>(null);
  const known = useMemo(
    () => points.map((p, i) => ({ ...p, i })).filter((p): p is LinePoint & { i: number; value: number } => p.value !== null),
    [points],
  );

  const geo = useMemo(() => {
    if (width <= 0 || known.length === 0) return null;
    const vals = known.map((p) => p.value);
    const ticks = niceTicks(Math.min(...vals), Math.max(...vals), { target: 4, integer: true });
    const plotW = Math.max(10, width - PAD.left - PAD.right);
    const plotH = HEIGHT - PAD.top - PAD.bottom;
    const x = linear([0, Math.max(1, points.length - 1)], [PAD.left, PAD.left + plotW]);
    const y = linear([ticks[0]!, ticks[ticks.length - 1]!], [PAD.top + plotH, PAD.top]);
    const line = known.map((p, j) => `${j ? "L" : "M"}${x(p.i).toFixed(1)},${y(p.value).toFixed(1)}`).join("");
    const base = PAD.top + plotH;
    const area = known.length > 1 ? `${line}L${x(known[known.length - 1]!.i).toFixed(1)},${base}L${x(known[0]!.i).toFixed(1)},${base}Z` : "";
    return { ticks, x, y, line, area, plotW, plotH, base, labels: sparseLabelIndices(points.length, plotW, 72) };
  }, [width, points, known]);

  const nearest = (clientX: number, rect: DOMRect) => {
    if (!geo) return null;
    const rel = clientX - rect.left - PAD.left;
    const i = Math.round((rel / geo.plotW) * Math.max(1, points.length - 1));
    return Math.min(points.length - 1, Math.max(0, i));
  };

  const onMove = (e: PointerEvent<SVGRectElement>) => setActive(nearest(e.clientX, (e.currentTarget.ownerSVGElement as SVGSVGElement).getBoundingClientRect()));
  const onKey = (e: KeyboardEvent<SVGSVGElement>) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight" && e.key !== "Home" && e.key !== "End") return;
    e.preventDefault();
    setActive((cur) => {
      const start = cur ?? points.length - 1;
      if (e.key === "Home") return 0;
      if (e.key === "End") return points.length - 1;
      return Math.min(points.length - 1, Math.max(0, start + (e.key === "ArrowLeft" ? -1 : 1)));
    });
  };

  const last = known[known.length - 1];
  const activePoint = active !== null ? points[active] : null;

  return (
    <div ref={ref} className="an-plot" style={{ height: HEIGHT }}>
      {geo ? (
        <svg
          width={width}
          height={HEIGHT}
          role="img"
          aria-label={`${seriesLabel}: ${known.length ? `${format(known[0]!.value)} to ${format(last!.value)}` : "no data"}. Use arrow keys to read each day.`}
          tabIndex={0}
          onKeyDown={onKey}
          onBlur={() => setActive(null)}
          className="an-svg"
        >
          {geo.ticks.map((t) => (
            <g key={t}>
              <line x1={PAD.left} x2={PAD.left + geo.plotW} y1={geo.y(t)} y2={geo.y(t)} stroke={CHROME.grid} strokeWidth={1} />
              <text x={PAD.left - 8} y={geo.y(t)} dy="0.32em" textAnchor="end" className="an-axis">
                {axisLabel(t)}
              </text>
            </g>
          ))}
          {geo.labels.map((i) => (
            <text key={i} x={geo.x(i)} y={HEIGHT - 8} textAnchor={i === 0 ? "start" : i === points.length - 1 ? "end" : "middle"} className="an-axis">
              {points[i]!.label}
            </text>
          ))}
          {geo.area ? <path d={geo.area} fill={SERIES_1} opacity={0.1} /> : null}
          <path d={geo.line} fill="none" stroke={SERIES_1} strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />
          {known.length <= 31
            ? known.map((p) => <circle key={p.key} cx={geo.x(p.i)} cy={geo.y(p.value)} r={2.5} fill={SERIES_1} />)
            : null}
          {last ? (
            <>
              <circle cx={geo.x(last.i)} cy={geo.y(last.value)} r={4} fill={SERIES_1} stroke={CHROME.surface} strokeWidth={2} />
              <text x={geo.x(last.i) + 8} y={geo.y(last.value)} dy="0.32em" className="an-endlabel">
                {axisLabel(last.value)}
              </text>
            </>
          ) : null}
          {active !== null ? (
            <g pointerEvents="none">
              <line x1={geo.x(active)} x2={geo.x(active)} y1={PAD.top} y2={geo.base} stroke={CHROME.axis} strokeWidth={1} />
              {activePoint?.value !== null && activePoint ? (
                <circle cx={geo.x(active)} cy={geo.y(activePoint.value)} r={5} fill={SERIES_1} stroke={CHROME.surface} strokeWidth={2} />
              ) : null}
            </g>
          ) : null}
          <rect
            x={PAD.left}
            y={PAD.top}
            width={geo.plotW}
            height={geo.plotH}
            fill="transparent"
            onPointerMove={onMove}
            onPointerLeave={() => setActive(null)}
          />
        </svg>
      ) : null}
      {geo && activePoint ? (
        <ChartTooltip
          x={geo.x(active!)}
          y={PAD.top}
          width={width}
          title={activePoint.label}
          rows={[{ color: SERIES_1, label: seriesLabel, value: activePoint.value === null ? "No snapshot" : format(activePoint.value) }]}
        />
      ) : null}
    </div>
  );
}
